import { useCardReader } from "application/contexts/cardReader/CardReaderContext";
import { useCatalogStore } from "application/contexts/catalog/CatalogStore";
import { useIdle } from "application/contexts/idle/IdleContext";
import { useKiosk } from "application/contexts/kiosk/KioskContext";
import { useCardReaderStore } from "modules/cardReader/infra/store/CardReaderStore";
import { ICardReaderStore } from "modules/cardReader/domain/interfaces/IUseCardReaderStore";
import {
  PrinterStore,
  usePrinterStore,
} from "application/contexts/printer/PrinterStore";
import { useScreenStore } from "application/contexts/screen/ScreenStore";
import { useUi } from "application/contexts/ui/UIContext";
import { ErrorCodeType } from "application/models/IError";
import { IFullCardData, IGetPendingRecords, IPendingRecordsResposne } from "domains/cashless/aggregates/ICardCashless";
import {
  OfflineRechargeStatus,
  Order,
} from "domains/order/agreggates/order/Order";
import { Payment } from "domains/order/agreggates/payment/payment";
import { TransactionType } from "domains/order/agreggates/payment/transactionType";
import { useTranslation } from "i18n/useTranslation";
import { ProdutoTipo } from "Infrastructure/interfaces/PedidoMeep";
import { CashlessRepository } from "Infrastructure/repositories/api/CashlessRepository";
import { useAppInsights } from "Infrastructure/repositories/appInsights/AppInsights";
import { LocalStorageCashlessOfflineRepository } from "Infrastructure/repositories/localStorage/LocalStorageCashlessOfflineRepository";
import { getOrderRepository } from "../../../Infrastructure/repositories/localStorage/getOrderRepository";
import { CefSharpCardReader } from "Infrastructure/services/cefSharpService/local/CefSharpCardReader";
import { CreatePrintOfflineRechargeUseCase } from "modules/order/application/useCases/CreatePrintOfflineRechargeUseCase";
import { IEmitedNfce } from "modules/order/domain/models/INfce";
import {
  IOfflineRechargeStatus,
  IOrderLog,
} from "modules/order/domain/models/IOrderLog";
import { IPedidoPosDTO } from "modules/order/domain/models/IPedidoPosDTO";
import { IOrderProviderRef } from "modules/order/presentation/OrderProvider";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Trans } from "react-i18next";
import { useHistory } from "react-router-dom";
import { GetError } from "utils/GetError";
import { v4 } from "uuid";
import { useShallow } from "zustand/react/shallow";
import { OfflineRechargeSteps } from "./OfflineRecharge.types";
import { ReactNativeCardReader } from "Infrastructure/services/reactNativeServices/ReactNativeCardReader";
import { useSession } from "application/contexts/session/SessionContext";
import { ICustomerIdentificationHandler } from "modules/order/presentation/customerIdentification/CustomerIdentification";
import { MeepOrderRepository } from "Infrastructure/repositories/api/OrderRepository";
import { keyBlockedCards } from "Infrastructure/repositories/api/KioskRepository";
import { Kiosk } from "domains/kiosk/aggregates/Kiosk";
import { isReactNative } from "utils/constants";

const cardReaderService = isReactNative
  ? ReactNativeCardReader()
  : CefSharpCardReader();

const isTagBlocked = (tag: string): boolean => {
  const raw = localStorage.getItem(keyBlockedCards);
  const blocked: string[] = raw ? JSON.parse(raw) : [];
  return blocked.some((b) => b.toLowerCase() === tag.toLowerCase());
};
const orderRepositoryDB = getOrderRepository();
const cashlessRepository = CashlessRepository();
const cashlessOfflineRepository = LocalStorageCashlessOfflineRepository();
const orderRepository = MeepOrderRepository();
const UseOfflineRecharge = (
  orderProvider: React.RefObject<IOrderProviderRef>
) => {
  const { kiosk } = useKiosk();
  const { sessaoIdPos } = useSession();
  const {
    isClosed,
    tag,
    offlineCardData,
    clearCardData,
    enableSensor,
    readErrorMessage,
    outOfDateError,
    cardDetected,
    disableSensor,
    setOfflineCardData,
  } = useCardReader();
  const setOfflineRechargeRecordMode = useCardReaderStore(
    (state: ICardReaderStore) => state.setOfflineRechargeRecordMode
  );
  const { toastFullscreen, toast } = useUi();
  const { getConfigurationProduct } = useCatalogStore();
  const { replace } = useHistory();
  const { addLog } = useAppInsights();
  const { handleActiveDefaultIdleTimer, resetCounter } = useIdle();
  const { resolution } = useScreenStore();
  const { t } = useTranslation();
  const customerRef = useRef<ICustomerIdentificationHandler>(null);

  const activationDisplayValue = React.useMemo(() => {
    const product = getConfigurationProduct(ProdutoTipo.AtivacaoCashless);
    return product?.price ?? product?.realPrice ?? 0;
  }, [getConfigurationProduct]);


  const [addOrdersToPrintQueue] = usePrinterStore(
    useShallow((state: PrinterStore) => [state.addOrders])
  );

  const navigation = useHistory();

  const [step, setStep] = useState<OfflineRechargeSteps>(
    OfflineRechargeSteps.READ
  );
  const [rechargeValue, setRechargeValue] = useState(0);
  const [rechargeORder, setRechargeORder] = useState<Order | null>(null);
  const [activeLoad, setActiveLoad] = useState<"reading" | "writing" | null>(
    null
  );


  const [pendingRechargeOrdersFromOnline, setPendingRechargeOrdersFromOnline] = useState<IPendingRecordsResposne | null>(null);

  const [showBackButtonRecord, setShowBackButtonRecord] = useState(false);
  const [showBackButtonPendingRecord, setShowBackButtonPendingRecord] = useState(false);

  const rechargeStarted = useRef(false);
  const firstLoad = useRef(true);
  const cardToRecharge = useRef<IFullCardData | null>(null);
  const isPendingRecord = useRef(false);

  const lastError = useRef<string | null>(null);

  useEffect(() => {
    handleActiveDefaultIdleTimer();
  }, [handleActiveDefaultIdleTimer]);

  const restart = useCallback(() => {
    setOfflineRechargeRecordMode(false);
    clearCardData();
    firstLoad.current = true;
    rechargeStarted.current = false;
    cardToRecharge.current = null;
    isPendingRecord.current = false;
    setStep(OfflineRechargeSteps.READ);
    setRechargeORder(null);
    setRechargeValue(0);
    setActiveLoad(null);
    setShowBackButtonRecord(false);
    setShowBackButtonPendingRecord(false);
    enableSensor();
    setPendingRechargeOrdersFromOnline(null);
  }, [clearCardData, enableSensor, setOfflineRechargeRecordMode, setPendingRechargeOrdersFromOnline]);

  useEffect(() => {
    enableSensor();
    return () => {
      disableSensor();
    };
  }, [enableSensor, disableSensor]);


  useEffect(() => {
    if (step === OfflineRechargeSteps.RECORD) {
      setOfflineRechargeRecordMode(true);
      enableSensor(undefined, true);
    }
    return () => {
      if (step === OfflineRechargeSteps.RECORD) {
        setOfflineRechargeRecordMode(false);
      }
    };
  }, [enableSensor, step, setOfflineRechargeRecordMode]);

  useEffect(() => {
    if (step === OfflineRechargeSteps.PENDING_RECORD) {
      enableSensor();
    }
    return () => {
      if (step === OfflineRechargeSteps.PENDING_RECORD) {
        disableSensor();
      }
    };
  }, [enableSensor, step, disableSensor]);

  // Exibir botão Voltar após 30s na tela de aguardar cartão para gravar
  useEffect(() => {
    if (step !== OfflineRechargeSteps.RECORD) {
      setShowBackButtonRecord(false);
      return;
    }
    setShowBackButtonRecord(false);
    const timer = setTimeout(() => setShowBackButtonRecord(true), 20000);
    return () => clearTimeout(timer);
  }, [step]);

  // Exibir botão Voltar após 30s na tela de gravar saldo pendente
  useEffect(() => {
    if (step !== OfflineRechargeSteps.PENDING_RECORD) {
      setShowBackButtonPendingRecord(false);
      return;
    }
    setShowBackButtonPendingRecord(false);
    const timer = setTimeout(() => setShowBackButtonPendingRecord(true), 20000);
    return () => clearTimeout(timer);
  }, [step]);
  // Erro de leitura
  useEffect(() => {
    resetCounter();
    if (readErrorMessage && readErrorMessage !== lastError.current) {
      lastError.current = readErrorMessage;
      const splited = readErrorMessage.split(":");
      if (splited[0] === "Err intr" && step === OfflineRechargeSteps.READ) {
        const message = (
          <div className="offline-recharce-success-message">
            <h3>
              <Trans i18nKey="offline_recharge.messages.read_error_title">
                <b>Erro de leitura</b> ou {{ reason: splited[1] }}
              </Trans>
            </h3>
            <div className="offline-recharce-success-message-div1">
              <p>{t("offline_recharge.messages.read_error_line1")}</p>
              <p>
                <b>{t("offline_recharge.messages.read_error_line2_bold")}</b>
              </p>
            </div>
            <p>
              <b>{t("offline_recharge.messages.read_error_footer_bold")}</b>
            </p>
          </div>
        );
        setTimeout(() => {
          toastFullscreen(message, "warning", {
            icon: "error_outline",
            iconSize: 200,
            time: 4000,
            buttonText: t("offline_recharge.messages.try_again"),
            onClose: () => restart(),
          });
          setActiveLoad(null);
          clearCardData();
        }, 1000);
        clearCardData();
      } else if (
        splited[0] === "Err intr" &&
        step === OfflineRechargeSteps.RECORD
      ) {
        toastFullscreen(
          t("offline_recharge.messages.record_mismatch"),
          "warning",
          { time: 3000 }
        );
        restart();
      } else if (step === OfflineRechargeSteps.RECORD) {
        toastFullscreen(
          t("offline_recharge.messages.record_failed"),
          "warning",
          { time: 3000 }
        );
        clearCardData();
        restart();
      } else {
        toastFullscreen(t("offline_recharge.messages.read_failed"), "warning", {
          time: 3000,
          onClose: () => {
            restart();
          }
        });
        //restart();
      }
    }
    if (!readErrorMessage) {
      lastError.current = null;
    }
  }, [
    readErrorMessage,
    toastFullscreen,
    clearCardData,
    toast,
    step,
    replace,
    resetCounter,
    restart,
    t,
  ]);

  // Erro de tipo de sistema
  useEffect(() => {
    if (outOfDateError) {
      resetCounter();
      const message = <div className="offline-recharce-success-message">
        <p>
          <b>{t("offline_recharge.messages.function_unavailable")}</b>
        </p>
        <div style={{ textAlign: "center" }}>{outOfDateError}</div>
      </div>
      toastFullscreen(message, "error", { iconSize: 150, time: 3000 });
      clearCardData();
      disableSensor();
      replace("/");
    }
  }, [clearCardData, disableSensor, outOfDateError, replace, resetCounter, t, toastFullscreen]);

  const setClosedCardData = useCallback(async (tag: string, kiosk: Kiosk) => {
    if (kiosk && tag) {
      const userData = await customerRef.current?.open(kiosk, "Recharge");
      if (userData) {
        setOfflineCardData({
          Tag: tag,
          Document: userData.document ?? tag,
          Balance: 0,
          AssociationId: v4(),
          SectionKey: kiosk.cashlessCode,
        });
      }
    }
  }, [setOfflineCardData]);

  // quando ler seta os dados do cartão fechado (abertura de conta)
  useEffect(() => {
    if (isClosed && kiosk && tag && step === OfflineRechargeSteps.READ) {
      setClosedCardData(tag, kiosk);
    }
  }, [isClosed, kiosk, tag, setClosedCardData, step]);

  useEffect(() => {
    if (isClosed && kiosk && tag && step === OfflineRechargeSteps.RECORD && rechargeORder) {
      console.log("[useOfflineRecharge] setOfflineCardData", rechargeORder);
      setOfflineCardData({
        Tag: tag,
        Document: rechargeORder.customer?.document ?? tag,
        Balance: 0,
        AssociationId: rechargeORder.customer?.associationId ?? "",
        SectionKey: kiosk?.cashlessCode ?? "",
      });
    }
  }, [isClosed, kiosk, rechargeORder, setOfflineCardData, step, tag]);

  // Adiciona log de pedido  IOrderLog
  const addOrderLog = useCallback(
    async (
      order: IOrderLog | null,
      message: string,
      pedidoPOS?: IPedidoPosDTO,
      payment?: Payment,
      nfce?: IEmitedNfce[]
    ) => {
      try {
        if (order) {
          let orderLog = { ...order, payment, pedidoPOS, nfce };
          addLog("cashlessOffline", { message, orderLog });
          await orderRepositoryDB.add(orderLog, message);
        }
      } catch (error) {
        GetError(
          error,
          "addOrderLog",
          "useOfflineRecharge",
          "Falha ao adicionar log",
          ErrorCodeType.GENERAL
        );
      }
    },
    [addLog]
  );

  // Checagem de gravação pendente
  const checkPendingRecharge = useCallback(async (associationId: string) => {
    const pendingRechargeOrder =
      await orderRepositoryDB.findPendingRecharge(associationId);
    if (
      pendingRechargeOrder &&
      pendingRechargeOrder.offlineRechargeStatus ===
      OfflineRechargeStatus.RECHARGE
    ) {
      try {
        const result = await cashlessRepository.getPendingRecharge({
          accountId: associationId,
          tag: pendingRechargeOrder?.customer?.tag ?? "",
          localId: kiosk?.localId,
        });
        addLog("cashlessOffline", {
          message: "Pending recharge",
          orderLog: { ...result, ...pendingRechargeOrder },
        });
        if (
          !pendingRechargeOrder.isSynchronized ||
          (result?.ordersId?.length &&
            result.ordersId.find((it) => it === pendingRechargeOrder.id))
        ) {
          isPendingRecord.current = true;
          setStep(OfflineRechargeSteps.RECORD);
          setRechargeORder(pendingRechargeOrder);
          cardToRecharge.current = {
            AssociationId: pendingRechargeOrder.customer?.associationId ?? "",
            Balance: pendingRechargeOrder.customer?.balance ?? 0,
            Document: pendingRechargeOrder.customer?.document ?? "",
            Tag: pendingRechargeOrder.customer?.tag ?? "",
            SectionKey: kiosk?.cashlessCode ?? "",
          };
        } else {
          const orderLog = {
            id: pendingRechargeOrder.id,
            offlineRechargeStatus: IOfflineRechargeStatus.COMPLETED,
          } as IOrderLog;
          await addOrderLog(
            orderLog,
            t("offline_recharge.messages.recording_completed_elsewhere")
          );
        }
      } catch (error) {
        addLog("cashlessOffline", {
          message: "Falha ao obter recarga pendente",
          orderLog: pendingRechargeOrder,
        });
      }
    }
  }, [addLog, addOrderLog, kiosk, t]);


  const writeOnlineBalance = useCallback(async (offlineCardData: IFullCardData, pendingRechargeOrdersFromOnline: IPendingRecordsResposne) => {
    if (pendingRechargeOrdersFromOnline && pendingRechargeOrdersFromOnline.ordersId && pendingRechargeOrdersFromOnline.ordersId.length > 0) {
      console.log("[useOfflineRecharge] writeOnlineBalance offlineCardData", offlineCardData);
      try {
        resetCounter();
        setActiveLoad("writing");
        rechargeStarted.current = true;
        const newBalanceCents = Math.round(
          offlineCardData.Balance + pendingRechargeOrdersFromOnline.rechargeAmount * 100
        );
        if (!isClosed) {
          console.log("[useOfflineRecharge] writeOnlineBalance writeBalance", offlineCardData);
          await cardReaderService.writeBalance(
            offlineCardData.Tag,
            newBalanceCents
          );
        } else {
          console.log("[useOfflineRecharge] writeOnlineBalance writeCardData", offlineCardData);
          await cardReaderService.writeCardData({
            Tag: offlineCardData.Tag,
            Balance: newBalanceCents,
            AssociationId: offlineCardData.AssociationId,
            Document: offlineCardData.Document,
            SectionKey: offlineCardData.SectionKey,
          });
        }
        try {
          pendingRechargeOrdersFromOnline.ordersId?.forEach(async (orderId) => {
            await cashlessOfflineRepository.markAsRecorded(orderId);
          })
          await cashlessRepository.confirmPendingRecord(pendingRechargeOrdersFromOnline.ordersId ?? []);
          await orderRepository.syncOrdersAsync(kiosk?.localId ?? "", kiosk?.id ?? "");

          const orders = await orderRepositoryDB.getOrdersByIds(pendingRechargeOrdersFromOnline.ordersId ?? []);

          orders.forEach(async (order) => {
            const orderLog: IOrderLog = {
              ...order,
              offlineRechargeStatus: IOfflineRechargeStatus.COMPLETED,
            } as IOrderLog;
            await addOrderLog(orderLog, `Gravação concluida com sucesso: ${order.customer?.tag}`);
          });

        } catch (error) {
          addLog("cashlessOffline", {
            message: "Falha ao confirmar gravação",
            orderLog: error,
          });
        }
        //const orderLog: IOrderLog = {
        //  ...order,
        //  offlineRechargeStatus: IOfflineRechargeStatus.COMPLETED,
        //  isDataRecorded: true,
        //};
        //await addOrderLog(orderLog, "Gravação concluida com sucesso");

        setTimeout(async () => {
          const message = (
            <div className="offline-recharce-success-message">
              <h3>
                <b>{t("offline_recharge.messages.write_success_title")}....</b>
              </h3>
              <p>{t("offline_recharge.messages.write_success_line1")}</p>
              <p>
                <b>
                  {t("offline_recharge.messages.write_success_line2_bold")}
                </b>
              </p>
            </div>
          );
          addOrdersToPrintQueue(
            CreatePrintOfflineRechargeUseCase(
              newBalanceCents / 100,
              Math.round(offlineCardData.Balance) / 100,
              "Recargas",
              offlineCardData.Tag,
              new Date().toISOString(),
              offlineCardData.Document,
              //ord?.customer?.name
            )
          );
          toastFullscreen(message, "success", {
            icon: "task_alt",
            iconSize: 200,
            time: 3000,
          });
          disableSensor();
          replace("/");
          setActiveLoad(null);
        }, 3000);
      } catch (error) {
        //await addOrderLog(
        //  order,
        //  `Falha ao gravar recarga no cartão: ${order.customer?.tag}`
        //);
        setActiveLoad(null);
        if (isPendingRecord.current) {
          const message = (
            <div className="offline-recharce-success-message">
              <h3>
                <b>{t("offline_recharge.messages.pending_found_title")}</b>
              </h3>
              <p>{t("offline_recharge.messages.write_failed_line1")}..</p>
              <p>
                <b>
                  {t("offline_recharge.messages.write_failed_line2_bold")}
                </b>
              </p>
            </div>
          );
          toastFullscreen(message, "warning", {
            icon: "error_outline",
            iconSize: 200,
            time: 4000,
            onClose: () => {
              restart();
            }
          });
          restart();
        } else {
          const message = (
            <div className="offline-recharce-success-message">
              <h3>
                <b>{t("offline_recharge.messages.write_failed_title")}...</b>
              </h3>
              <p>{t("offline_recharge.messages.write_failed_line1")}</p>
              <p>
                <b>
                  {t("offline_recharge.messages.write_failed_line2_bold")}
                </b>
              </p>
            </div>
          );
          toastFullscreen(message, "warning", {
            icon: "error_outline",
            iconSize: 200,
            time: 3000,
            buttonText: t("offline_recharge.messages.try_again"),
            onClose: () => {
              restart();
            }
          });
          clearCardData();
          setStep(OfflineRechargeSteps.RECORD);
          restart();
        }
      }
    }
  }, [addLog, addOrderLog, addOrdersToPrintQueue, clearCardData, disableSensor, isClosed, kiosk, replace, resetCounter, restart, t, toastFullscreen]);


  const getOnlinePendentRecharge = useCallback(async (offlineCardData: IFullCardData) => {
    try {
      const pendingRechargeOrdersFromOnline: IPendingRecordsResposne = await cashlessRepository.getPendingRecharge({
        accountId: offlineCardData.AssociationId,
        tag: offlineCardData.Tag,
        localId: kiosk?.localId,
      });
      if (pendingRechargeOrdersFromOnline && pendingRechargeOrdersFromOnline.ordersId && pendingRechargeOrdersFromOnline.ordersId.length > 0) {
        setStep(OfflineRechargeSteps.PENDING_RECORD);
        setPendingRechargeOrdersFromOnline(pendingRechargeOrdersFromOnline);
      }
    } catch (error) {
      addLog("cashlessOffline", {
        message: "Falha ao obter recarga pendente",
        orderLog: error,
      });
    }

  }, [kiosk, addLog]);


  useEffect(() => {
    if (step === OfflineRechargeSteps.PENDING_RECORD && offlineCardData && pendingRechargeOrdersFromOnline) {
      writeOnlineBalance(offlineCardData, pendingRechargeOrdersFromOnline);
    }
  }, [step, offlineCardData, pendingRechargeOrdersFromOnline, writeOnlineBalance]);

  // Verifica se existem pedidos pendentes de gravação e escreve o saldo no cartão
  const verifyNotSincronizedPendentWriteBalance = useCallback(async (offlineCardData: IFullCardData) => {
    const pendingRechargeOrdersFromOffline = await cashlessOfflineRepository.getNotSincronizedOfflineRechargesAndNotRecordedByAssociationId(offlineCardData.AssociationId);
    console.log("[useOfflineRecharge] pendingRechargeOrdersFromOffline", pendingRechargeOrdersFromOffline);
    if (pendingRechargeOrdersFromOffline && pendingRechargeOrdersFromOffline.length > 0) {
      const pendingRechargeOrder = pendingRechargeOrdersFromOffline[0];
      if (pendingRechargeOrder.pedidoPOSId) {
        const pendingRechargeOrderLog = await orderRepositoryDB.findById(pendingRechargeOrder.pedidoPOSId);
        console.log("[useOfflineRecharge] pendingRechargeOrderLog", pendingRechargeOrderLog);
        if (pendingRechargeOrderLog) {
          isPendingRecord.current = true;
          setStep(OfflineRechargeSteps.RECORD);
          setRechargeORder(pendingRechargeOrderLog);
        }
      }
    } else {
      getOnlinePendentRecharge(offlineCardData);
    }
  }, [getOnlinePendentRecharge]);

  useEffect(() => {
    console.log("[useOfflineRecharge] useEffect", offlineCardData?.AssociationId, firstLoad.current);
    if (offlineCardData?.AssociationId && firstLoad.current) {
      console.log("[useOfflineRecharge] offlineCardData", offlineCardData);
      resetCounter();
      //checkPendingRecharge(offlineCardData.AssociationId);
      verifyNotSincronizedPendentWriteBalance(offlineCardData);
    }
  }, [checkPendingRecharge, offlineCardData, resetCounter, verifyNotSincronizedPendentWriteBalance]);

  //Gravação do novo saldo no cartão
  const writeBalance = useCallback(
    async (order: Order) => {
      console.log("[useOfflineRecharge] writeBalance order", order);
      if (!!offlineCardData && cardToRecharge.current) {
        if (
          offlineCardData.AssociationId === cardToRecharge.current.AssociationId
        ) {
          await addOrderLog(
            order,
            `Gravação iniciada - Tag: ${order.customer?.tag}`
          );

          let valueToCredit = order.totalValue;
          if (isClosed) {
            valueToCredit = order.totalValue - (order.activationValue ?? 0);
          }
          const newBalanceCents = Math.round(
            offlineCardData.Balance + valueToCredit * 100
          );

          console.log("offlineCardData", offlineCardData);
          try {
            setActiveLoad("writing");
            if (!isClosed) {
              await cardReaderService.writeBalance(
                offlineCardData.Tag,
                newBalanceCents
              );
            } else {
              await cardReaderService.writeCardData({
                Tag: offlineCardData.Tag,
                Balance: newBalanceCents,
                AssociationId: offlineCardData.AssociationId,
                Document: offlineCardData.Document,
                SectionKey: offlineCardData.SectionKey,
              });
            }
            try {
              await cashlessOfflineRepository.markAsRecorded(order.id);
              await orderRepository.syncOrdersAsync(kiosk?.localId ?? "", kiosk?.id ?? "");
            } catch (error) {
              addLog("cashlessOffline", {
                message: "Falha ao confirmar gravação",
                orderLog: error,
              });
            }
            const orderLog: IOrderLog = {
              ...order,
              offlineRechargeStatus: IOfflineRechargeStatus.COMPLETED,
              isDataRecorded: true,
            };
            await addOrderLog(orderLog, "Gravação concluida com sucesso");

            setTimeout(async () => {
              const message = (
                <div className="offline-recharce-success-message">
                  <h3>
                    <b>{t("offline_recharge.messages.write_success_title")}....</b>
                  </h3>
                  <p>{t("offline_recharge.messages.write_success_line1")}</p>
                  <p>
                    <b>
                      {t("offline_recharge.messages.write_success_line2_bold")}
                    </b>
                  </p>
                </div>
              );
              addOrdersToPrintQueue(
                CreatePrintOfflineRechargeUseCase(
                  newBalanceCents / 100,
                  Math.round(offlineCardData.Balance) / 100,
                  order.friendlyId,
                  offlineCardData.Tag,
                  order.createdAt,
                  offlineCardData.Document,
                  order?.customer?.name
                )
              );
              toastFullscreen(message, "success", {
                icon: "task_alt",
                iconSize: 200,
                time: 3000,
              });
              disableSensor();
              replace("/");
              setActiveLoad(null);
            }, 3000);
          } catch (error) {
            await addOrderLog(
              order,
              `Falha ao gravar recarga no cartão: ${order.customer?.tag}`
            );
            setActiveLoad(null);
            if (isPendingRecord.current) {
              const message = (
                <div className="offline-recharce-success-message">
                  <h3>
                    <b>{t("offline_recharge.messages.pending_found_title")}</b>
                  </h3>
                  <p>{t("offline_recharge.messages.write_failed_line1")}..</p>
                  <p>
                    <b>
                      {t("offline_recharge.messages.write_failed_line2_bold")}
                    </b>
                  </p>
                </div>
              );
              toastFullscreen(message, "warning", {
                icon: "error_outline",
                iconSize: 200,
                time: 4000,
                onClose: () => {
                  restart();
                }
              });
              restart();
            } else {
              const message = (
                <div className="offline-recharce-success-message">
                  <h3>
                    <b>{t("offline_recharge.messages.write_failed_title")}...</b>
                  </h3>
                  <p>{t("offline_recharge.messages.write_failed_line1")}</p>
                  <p>
                    <b>
                      {t("offline_recharge.messages.write_failed_line2_bold")}
                    </b>
                  </p>
                </div>
              );
              toastFullscreen(message, "warning", {
                icon: "error_outline",
                iconSize: 200,
                time: 3000,
                buttonText: t("offline_recharge.messages.try_again"),
                onClickButton: () => {
                  restart();
                },
                onClose: () => {
                  restart();
                }
              });
              clearCardData();
            }
          }
        } else {
          toastFullscreen(
            t("offline_recharge.messages.record_mismatch"),
            "warning",
            { time: 3000 }
          );
          isPendingRecord.current = false;
          await addOrderLog(order, `Cartão incorreto: ${offlineCardData.Tag}`);
          disableSensor();
          replace("/");
        }
      }
      isPendingRecord.current = false;
    },
    [offlineCardData, addOrderLog, isClosed, kiosk, addLog, t, addOrdersToPrintQueue, toastFullscreen, disableSensor, replace, restart, clearCardData]
  );

  //escrever saldo somando os pedidos pendentes de gravação



  useEffect(() => {
    // Checagem de leitura para gravação do cartão
    console.log("[useOfflineRecharge] offlineCardData", offlineCardData);
    console.log("[useOfflineRecharge] cardToRecharge.current", cardToRecharge.current);
    console.log("[useOfflineRecharge] step", step);
    console.log("[useOfflineRecharge] rechargeORder", rechargeORder);
    console.log("[useOfflineRecharge] rechargeStarted.current", rechargeStarted.current);
    console.log("[useOfflineRecharge] isClosed", isClosed);

    if (
      !!offlineCardData &&
      cardToRecharge.current &&
      step === OfflineRechargeSteps.RECORD &&
      rechargeORder &&
      !rechargeStarted.current
    ) {
      if (isTagBlocked(offlineCardData.Tag)) {
        toast("Cartão/Pulseira bloqueado", "error");
        clearCardData();
        rechargeStarted.current = false;
        return;
      }
      // Validação SectionKey apenas na recarga (não na abertura de conta): cartão deve ser do mesmo setor do totem
      if (!isClosed && kiosk?.cashlessCode != null && offlineCardData.SectionKey !== kiosk.cashlessCode) {
        toastFullscreen(
          t("offline_recharge.messages.section_key_mismatch"),
          "warning",
          { time: 4000, onClose: () => restart() }
        );
        clearCardData();
        rechargeStarted.current = false;
        return;
      }
      console.log("[useOfflineRecharge] writeBalance rechargeORder", rechargeORder);
      resetCounter();
      rechargeStarted.current = true;
      writeBalance(rechargeORder);
    }
  }, [isClosed, offlineCardData, rechargeORder, resetCounter, step, writeBalance, kiosk, toastFullscreen, t, restart, clearCardData]);

  // TODO: Adicionar model pixTransaction opcional
  const createOfflineRechargeOrder = useCallback(
    (value: number, cardDataOffline: IFullCardData, isClosed?: boolean) => {
      if (getConfigurationProduct(ProdutoTipo.Recarga)) {
        const activationProduct = getConfigurationProduct(ProdutoTipo.AtivacaoCashless);

        const newOrderId = v4();
        const newOrder: Order = {
          sectionKey: cardDataOffline.SectionKey,
          isClosed: isClosed,
          activationValue: activationProduct?.price ?? activationProduct?.realPrice ?? 0,
          isOfflineRecharge: true, // Recarga cashless offline
          isDataRecorded: false, // Pendente de gravação
          isOfflineRechargeRecordConfirmed: false, // Gravação concluída
          cartDate: new Date().toISOString(),
          id: newOrderId,
          sessionId: sessaoIdPos,
          orderItems: [
            {
              id: v4(),
              name: getConfigurationProduct(ProdutoTipo.Recarga)?.name ?? "",
              category: "",
              price: value,
              realPrice: value,
              totalPrice: value,
              description: null,
              productPrice: value,
              productId: getConfigurationProduct(ProdutoTipo.Recarga)?.id ?? "",
              productType: ProdutoTipo.Recarga,
              quantity: 1,
              imageUrl: "",
              thumbnailUrl: "",
              hideCatalog: false,
              barcode: null,
              orderComposition: null,
              compositions: null,
              printerName: null,
              translations: [],
            },
          ],
          totalValue: value + (isClosed ? activationProduct?.price ?? activationProduct?.realPrice ?? 0 : 0),
          friendlyId:
            kiosk?.kioskName.replace(/\D/gm, "") +
            " " +
            newOrderId.substring(0, 4),
          createdAt: new Date().toISOString(),
          customer: {
            orderId: newOrderId,
            tag: cardDataOffline.Tag,
            number: cardDataOffline.Tag,
            orderPad: cardDataOffline.Tag,
            document: cardDataOffline.Document,
            balance: cardDataOffline.Balance,
            associationId: cardDataOffline.AssociationId,
          },
        };
        return newOrder;
      } else {
        toastFullscreen(
          t("offline_recharge.messages.config_products_missing"),
          "error"
        );
      }
    },
    [getConfigurationProduct, kiosk, toastFullscreen, t, sessaoIdPos]
  );

  // Inicio do pagamento da recarga
  const paymentCashlessOffline = useCallback(
    async (type: TransactionType) => {
      if (rechargeValue > 0 && offlineCardData) {
        const newOrder = createOfflineRechargeOrder(
          rechargeValue / 100,
          offlineCardData,
          isClosed
        );
        if (newOrder) {
          const orderLog: IOrderLog = {
            ...newOrder,
            offlineRechargeStatus: IOfflineRechargeStatus.PAYMENT,
          };
          await addOrderLog(
            orderLog,
            `Recarga offline: Tipo pgto: ${TransactionType[type]}, Tag: ${newOrder.customer?.tag}`
          );
          try {
            const result = await orderProvider.current?.startPayment(
              newOrder,
              "Recharge",
              type
            );
            if (result) {
              if (!isClosed) {
                console.log("[useOfflineRecharge] paymentCashlessOffline clearCardData");
                clearCardData();
              }
              setStep(OfflineRechargeSteps.RECORD);
              setRechargeORder({
                ...result.order,
                payment: result.payment,
              });
              const orderLog: IOrderLog = {
                ...newOrder,
                offlineRechargeStatus: IOfflineRechargeStatus.RECHARGE,
              };
              await addOrderLog(
                orderLog,
                `Gravação pendente - Tag: ${newOrder.customer?.tag}`
              );
            } else {
              await addOrderLog(
                orderLog,
                `Gravação pendente - Tag: ${newOrder.customer?.tag}`
              );
              toast(t("offline_recharge.messages.payment_failed"), "error");
              GetError(
                new Error("Falha no pagamento"),
                "paymentCashlessOffline",
                "useOfflineRecharge",
                undefined,
                ErrorCodeType.PAYMENT
              );
            }
          } catch (error) {
            GetError(
              error,
              "paymentCashlessOffline",
              "useOfflineRecharge",
              undefined,
              ErrorCodeType.PAYMENT
            );
          } finally {
            resetCounter();
          }
        }
      } else {
        toast(t("offline_recharge.messages.invalid_value"), "warning");
      }
    },
    [rechargeValue, offlineCardData, createOfflineRechargeOrder, addOrderLog, orderProvider, isClosed, clearCardData, toast, t, resetCounter]
  );

  useEffect(() => {
    if (!offlineCardData && firstLoad.current && cardDetected) {
      resetCounter();
      setActiveLoad("reading");
      setTimeout(() => {
        setActiveLoad(null);
      }, 3500);
    }
  }, [cardDetected, offlineCardData, resetCounter]);

  useEffect(() => {
    if (!!offlineCardData && firstLoad.current && !activeLoad) {
      if (isTagBlocked(offlineCardData.Tag)) {
        toast("Cartão/Pulseira bloqueado", "error");
        clearCardData();
        return;
      }
      // Validação apenas no cashless offline: SectionKey do cartão deve ser igual ao cashlessCode do totem
      if (kiosk?.cashlessCode != null && offlineCardData.SectionKey !== kiosk.cashlessCode) {
        toastFullscreen(
          t("offline_recharge.messages.section_key_mismatch"),
          "warning",
          { time: 4000, onClose: () => restart() }
        );
        clearCardData();
        return;
      }
      resetCounter();
      cardToRecharge.current = offlineCardData;
      setStep(OfflineRechargeSteps.PAY_RECHARGE);
      firstLoad.current = false;
    }
  }, [offlineCardData, resetCounter, activeLoad, kiosk, toastFullscreen, t, restart, clearCardData]);

  const onPressKeyboard = useCallback((key: string) => {
    setRechargeValue((prev) => {
      const str = `${prev}${key}`;

      if (str.length > 6) {
        const shifted = str.slice(1);
        return Number(shifted);
      }

      return Number(str);
    });
  }, []);

  const onPressBack = useCallback(() => {
    setRechargeValue((prev) => {
      const str = prev.toString();
      const sliced = str.slice(0, -1);
      const cleaned = sliced === "" ? "0" : sliced;
      return Number(cleaned);
    });
  }, []);

  const addPreviousValue = useCallback((valueInReais: number) => {
    setRechargeValue((prev) => {
      const cents = prev % 100;
      const main = Math.floor(prev / 100);

      const newMain = main + valueInReais;
      const result = newMain * 100 + cents;

      return result;
    });
  }, []);


  const openOfflineCard = useCallback(async () => {
    if (!kiosk) {
      return;
    }
    const accountId = v4();
    const customer = await customerRef.current?.open(kiosk, "Recharge");
    if (customer) {
      console.log("customer", customer);
    }
  }, [kiosk]);

  //useEffect(() => {
  //  if (isClosed) {
  //    openOfflineCard();
  //  }
  //}, [isClosed, openOfflineCard]);

  useEffect(() => {
    if (step === OfflineRechargeSteps.READ) {
      enableSensor();
    }
  }, [enableSensor, step]);


  const onClickBack = useCallback(() => {
    cardToRecharge.current = null;
    firstLoad.current = true;
    clearCardData();

    if (step === OfflineRechargeSteps.PAY_RECHARGE) {
      setStep(OfflineRechargeSteps.READ);
    } else {
      disableSensor();
      navigation.replace("/");
    }
  }, [clearCardData, disableSensor, navigation, step]);

  const onCancelRecord = useCallback(() => {
    restart();
    navigation.replace("/");
  }, [restart, navigation]);

  return {
    onPressBack,
    onPressKeyboard,
    paymentCashlessOffline,
    step,
    offlineCardData,
    rechargeValue,
    kiosk,
    onClickBack,
    activeLoad,
    resolution,
    addPreviousValue,
    t,
    customerRef,
    isClosed,
    activationDisplayValue,
    showBackButtonRecord,
    showBackButtonPendingRecord,
    onCancelRecord,
  };
};

export default UseOfflineRecharge;
